import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { notify } from '@/lib/toast';
import { Plus, Edit, Trash2, Users, Phone, Mail, MapPin, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { SearchInput } from '@/components/ui/search-input';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

const defaultForm = {
  name: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  ntn: '',
  notes: '',
};

interface SupplierRow {
  id: string;
  name: string;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  ntn?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

interface SupplierPayload {
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  ntn?: string;
  notes?: string;
}

export function SuppliersPage() {
  const { t } = useTranslation('purchases');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState<SupplierRow | null>(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({
    open: false,
    id: '',
    name: '',
  });
  const queryClient = useQueryClient();
  const handlePurchaseUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['suppliers'] });
  }, [queryClient]);
  useSocketEvent('purchase.received', handlePurchaseUpdate);
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('purchases:write') || hasPermission('purchases:*') || hasPermission('suppliers:write') || hasPermission('suppliers:*');
  const canUpdate = hasPermission('purchases:write') || hasPermission('purchases:*') || hasPermission('suppliers:write') || hasPermission('suppliers:*');
  const canDelete = hasPermission('purchases:write') || hasPermission('purchases:*') || hasPermission('suppliers:write') || hasPermission('suppliers:*');

  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 30_000,
  });
  const isTestMode: boolean = tenantData?.testMode ?? false;

  useEffect(() => {
    if (dialogOpen && editSupplier) {
      setForm({
        name: editSupplier.name ?? '',
        contactName: editSupplier.contactName ?? '',
        email: editSupplier.email ?? '',
        phone: editSupplier.phone ?? '',
        address: editSupplier.address ?? '',
        city: editSupplier.city ?? '',
        ntn: editSupplier.ntn ?? '',
        notes: editSupplier.notes ?? '',
      });
    } else if (dialogOpen) {
      setForm({ ...defaultForm });
    }
  }, [dialogOpen, editSupplier]);

  const { data, isLoading } = useQuery({
    queryKey: ['suppliers', search, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      const res = await api.get(`/suppliers?${params}`);
      return res.data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: SupplierPayload = {
        name: form.name,
        contactName: form.contactName || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        ntn: form.ntn || undefined,
        notes: form.notes || undefined,
      };
      if (editSupplier) {
        const { data } = await api.patch(`/suppliers/${editSupplier.id}`, payload);
        return data;
      }
      const { data } = await api.post('/suppliers', payload);
      return data;
    },
    onSuccess: () => {
      editSupplier ? notify.updated('Supplier') : notify.created('Supplier');
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setDialogOpen(false);
    },
    onError: (error: unknown) => {
      notify.error(error, 'Save failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/suppliers/${id}`),
    onSuccess: () => {
      notify.deleted('Supplier');
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
    onError: (err: unknown) => notify.error(err, 'Cannot delete supplier with open purchase orders'),
  });

  const suppliersList: SupplierRow[] = data?.data ?? [];
  const total = data?.total ?? data?.meta?.total ?? 0;

  const updateField = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-6 page-enter">
      {/* Header */}
      <PageHeader
        title={t('suppliersTitle')}
        subtitle={t('suppliersSubtitle')}
        action={
          canCreate && (
            <Button
              variant="gradient"
              onClick={() => { setEditSupplier(null); setDialogOpen(true); }}
            >
              <Plus className="me-2 h-4 w-4" />
              {t('addSupplier')}
            </Button>
          )
        }
      />

      {isTestMode && (
        <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-text">
          <FlaskConical className="h-4 w-4 shrink-0" />
          <span><strong>Test Mode:</strong> You are viewing test suppliers. Switch to Production in <a href="/settings" className="underline">Settings</a> to manage live suppliers.</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-1">
        <StatCard label={t('totalSuppliers')} value={total} format="number" icon={Users} />
      </div>

      {/* Table */}
      <Card className="animate-fade-in-up stagger-3">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t('allSuppliers')}</CardTitle>
            <SearchInput
              value={search}
              onChange={(value) => { setSearch(value); setPage(1); }}
              placeholder={t('searchSuppliersPlaceholder')}
              className="w-64"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <SkeletonTable rows={5} cols={8} />
            </div>
          ) : suppliersList.length === 0 ? (
            <EmptyState
              icon={Users}
              title={t('noSuppliersFound')}
              description={t('noSuppliersFoundDesc')}
              action={canCreate ? { label: t('addSupplier'), onClick: () => { setEditSupplier(null); setDialogOpen(true); } } : undefined}
            />
          ) : (
          <Table mobileCards>
            <THead>
              <Tr hover={false}>
                <Th>{t('name')}</Th>
                <Th>{t('contact')}</Th>
                <Th>{t('phone')}</Th>
                <Th>{t('email')}</Th>
                <Th>{t('city')}</Th>
                <Th>{t('ntn')}</Th>
                <Th>{t('status')}</Th>
                <Th className="text-end">{t('actions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {suppliersList.map((supplier: SupplierRow) => (
                  <Tr key={supplier.id}>
                    <Td className="font-medium td-name">{supplier.name}</Td>
                    <Td label={t('contact')} className="text-muted-foreground td-hide">{supplier.contactName || '—'}</Td>
                    <Td label={t('phone')} className="text-muted-foreground">
                      {supplier.phone ? (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {supplier.phone}
                        </span>
                      ) : '—'}
                    </Td>
                    <Td label={t('email')} className="text-muted-foreground td-hide">
                      {supplier.email ? (
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" /> {supplier.email}
                        </span>
                      ) : '—'}
                    </Td>
                    <Td label={t('city')} className="text-muted-foreground td-hide">
                      {supplier.city ? (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {supplier.city}
                        </span>
                      ) : '—'}
                    </Td>
                    <Td label={t('ntn')} className="text-muted-foreground td-hide">{supplier.ntn || '—'}</Td>
                    <Td label={t('status')}>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        supplier.isActive !== false
                          ? 'bg-success/10 text-success-text'
                          : 'bg-muted text-foreground'
                      }`}>
                        {supplier.isActive !== false ? t('active') : t('inactive')}
                      </span>
                    </Td>
                    <Td className="text-end td-actions">
                      <TableRowActions
                        actions={[
                          {
                            key: 'edit',
                            label: t('editSupplier'),
                            icon: Edit,
                            onSelect: () => { setEditSupplier(supplier); setDialogOpen(true); },
                            permission: canUpdate,
                            primary: true,
                          },
                          {
                            key: 'delete',
                            label: t('deleteSupplier'),
                            icon: Trash2,
                            onSelect: () => setDeleteConfirm({ open: true, id: supplier.id, name: supplier.name }),
                            permission: canDelete,
                            variant: 'destructive',
                          },
                        ]}
                      />
                    </Td>
                  </Tr>
                  ))
              }
            </TBody>
          </Table>
          )}

          {/* Pagination */}
          {total > 0 && (
            <div className="border-t px-4 py-3">
              <PaginationBar page={page} pageSize={20} total={total} onPageChange={setPage} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Supplier Dialog */}
      <FormDialog
        open={dialogOpen}
        title={editSupplier ? t('editSupplier') : t('addSupplier')}
        width="52rem"
        submitLabel={editSupplier ? t('updateSupplier') : t('createSupplier')}
        cancelLabel={t('cancel')}
        isPending={saveMutation.isPending}
        onClose={() => setDialogOpen(false)}
        onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }}
        submitDisabled={!form.name}
      >
        <div>
          <Label>{t('nameRequired')}</Label>
          <Input
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Supplier name"
            required
          />
        </div>

        <div>
          <Label>{t('contactName')}</Label>
          <Input
            value={form.contactName}
            onChange={(e) => updateField('contactName', e.target.value)}
            placeholder="Contact person name"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t('email')}</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="Email address"
            />
          </div>
          <div>
            <Label>{t('phone')}</Label>
            <Input
              dir="ltr"
              value={form.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              placeholder="Phone number"
            />
          </div>
        </div>

        <div>
          <Label>{t('address')}</Label>
          <Input
            value={form.address}
            onChange={(e) => updateField('address', e.target.value)}
            placeholder="Street address"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t('city')}</Label>
            <Input
              value={form.city}
              onChange={(e) => updateField('city', e.target.value)}
              placeholder="City"
            />
          </div>
          <div>
            <Label>{t('ntnTaxNumber')}</Label>
            <Input
              dir="ltr"
              value={form.ntn}
              onChange={(e) => updateField('ntn', e.target.value)}
              placeholder="NTN"
            />
          </div>
        </div>

        <div>
          <Label>{t('notes')}</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            placeholder="Additional notes"
            rows={3}
          />
        </div>
      </FormDialog>
      <ConfirmDialog
        open={deleteConfirm.open}
        title={t('deleteSupplier')}
        message={`Are you sure you want to delete "${deleteConfirm.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(deleteConfirm.id, {
            onSettled: () => setDeleteConfirm({ open: false, id: '', name: '' }),
          });
        }}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
